/* Shared utilities, constants and helpers for the Shimla QoH POS app. */

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GST_RATE = 0.05;

export const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtSec = (s) => (s > 60 ? `${(s / 60).toFixed(1)} min` : `${Math.round(s)} sec`);

export const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong";
};

/* Cart-line helpers — describe, key and price a configured line */
export const configuredUnitPrice = (basePrice, variant, addons) => {
  const v = variant ? Number(variant.price_delta || 0) : 0;
  const sv = variant ? (variant.sub_variants || []).reduce((s, x) => s + Number(x.price_delta || 0), 0) : 0;
  const a = (addons || []).reduce((s, x) => s + Number(x.price || 0) * Number(x.quantity || 1), 0);
  return Number(basePrice || 0) + v + sv + a;
};
export const describeChoice = (variant, addons) => {
  const parts = [];
  if (variant) {
    let vLabel = variant.name;
    const svs = (variant.sub_variants || []).map((sv) => sv.name).filter(Boolean);
    if (svs.length) vLabel += ` · ${svs.join(" + ")}`;
    parts.push(vLabel);
  }
  for (const a of (addons || [])) parts.push(`+${a.name}${a.quantity > 1 ? ` ×${a.quantity}` : ""}`);
  return parts.join(", ");
};
export const cartLineKey = (menuId, variant, addons) => {
  const v = variant ? variant.id : "-";
  const sv = variant ? (variant.sub_variants || []).map((x) => x.id).sort().join("+") : "-";
  const a = (addons || []).map((x) => `${x.id}x${x.quantity}`).sort().join(",");
  return `${menuId}|${v}|${sv}|${a}`;
};

/* POS tile background images per category */
export const IMG_BY_CATEGORY = {
  Steaks: "https://images.unsplash.com/photo-1546964124-0cce460f38ef?w=500",
  Burgers: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=500",
  Pasta: "https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=500",
  Risotto: "https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=500",
  Fries: "https://images.unsplash.com/photo-1541592106381-b31e9677c0e5?w=500",
  "Fried Chicken": "https://images.unsplash.com/photo-1626082927389-6cd097cdc6ec?w=500",
  Tempura: "https://images.unsplash.com/photo-1615361200141-f45040f367be?w=500",
  Pizzas: "https://images.unsplash.com/photo-1548365328-9f547fb0953b?w=500",
  Flatbreads: "https://images.unsplash.com/photo-1601050690597-df0568f70950?w=500",
  Naan: "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=500",
  Kebabs: "https://images.unsplash.com/photo-1544025162-d76694265947?w=500",
  Tikka: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=500",
  "Stir-fry": "https://images.unsplash.com/photo-1512058564366-18510be2db19?w=500",
  Noodles: "https://images.unsplash.com/photo-1552611052-33e04de081de?w=500",
  "Fried Rice": "https://images.unsplash.com/photo-1603133872878-684f208fb84b?w=500",
  Sushi: "https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=500",
  Sashimi: "https://images.unsplash.com/photo-1580822184713-fc5400e7fe10?w=500",
  Rolls: "https://images.unsplash.com/photo-1553621042-f6e147245754?w=500",
  Salads: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=500",
  "Cold Apps": "https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=500",
  Charcuterie: "https://images.unsplash.com/photo-1626200419199-391ae4be7a41?w=500",
  Cheese: "https://images.unsplash.com/photo-1552767059-ce182ead6c1b?w=500",
  Desserts: "https://images.unsplash.com/photo-1551024506-0bccd828d307?w=500",
  Pastries: "https://images.unsplash.com/photo-1509365465985-25d11c17e812?w=500",
  Breads: "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=500",
  Buns: "https://images.unsplash.com/photo-1620921575116-fb8902865bb2?w=500",
  Cocktails: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=500",
  Spirits: "https://images.unsplash.com/photo-1527281400683-1aefee6bca6b?w=500",
  Coffee: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=500",
  Tea: "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=500",
  Juices: "https://images.unsplash.com/photo-1622597467836-f3285f2131b8?w=500",
  Smoothies: "https://images.unsplash.com/photo-1553530666-ba11a7da3888?w=500",
};
export const itemImage = (cat) => IMG_BY_CATEGORY[cat] || "https://images.unsplash.com/photo-1547592180-85f173990554?w=500";


/* Menu Builder — factory for an empty item form */
export const emptyForm = (cats) => ({
  name: "", category: cats[0] || "Mains", price: "", description: "",
  variants: [], addons: [],
});

/* Cashier module gates */
export const ALL_CASHIER_MODULES = [
  ["pos", "Point of Sale"],
  ["kitchen", "Kitchen Display"],
  ["summary", "Daily Summary"],
  ["menu", "Menu Builder"],
  ["categories", "Categories"],
  ["stations", "Station Admin"],
  ["tables", "Tables"],
  ["manager", "Manager View"],
  ["reports", "Reports"],
];
export const DEFAULT_CASHIER_MODULES = ["pos", "kitchen", "summary"];

